export const DURATION_OPTIONS_KLING = [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const
export const DURATION_OPTIONS_WAN = [2, 3, 4, 5, 6, 7, 8, 9, 10] as const
export const DURATION_OPTIONS_SEEDANCE = [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const

export function durationOptionsForProvider(provider: string): readonly number[] {
  switch (provider) {
    case "wan":
    case "wan_video":
      return DURATION_OPTIONS_WAN
    case "openrouter_video":
    case "byteplus_seedance_video":
      return DURATION_OPTIONS_SEEDANCE
    default:
      return DURATION_OPTIONS_KLING
  }
}

export type DurationModelLike = {
  id?: string
  provider?: string
  durations?: readonly number[] | null
  wire?: string
  max_video_ref_seconds?: number | null
  max_total_video_ref_seconds?: number | null
  max_audio_refs?: number | null
  max_audio_ref_seconds?: number | null
  max_total_audio_ref_seconds?: number | null
}

function normalizeDurationOptions(raw: readonly number[]): number[] {
  const unique = new Set<number>()
  for (const n of raw) {
    const v = Number(n)
    if (Number.isFinite(v) && v > 0) unique.add(Math.round(v))
  }
  return Array.from(unique).sort((a, b) => a - b)
}

/** Prefer model catalog `durations`; fall back to provider-based lists. */
export function durationOptionsForModel(model: DurationModelLike | null | undefined): readonly number[] {
  if (model?.durations && model.durations.length > 0) {
    return normalizeDurationOptions(model.durations)
  }
  if (model?.provider) return durationOptionsForProvider(model.provider)
  return durationOptionsForProvider("kling")
}

export function durationOptionsForCanvasModel(model: DurationModelLike): readonly number[] {
  return durationOptionsForModel(model)
}

type ProjectShotVideoLike = {
  video_generation_provider?: string | null
  effective_video_generation_provider?: string | null
  effective_video_model?: string | null
}

/** Options for the project's effective shot-video model from workspace catalog. */
export function durationOptionsForProjectShotVideo(
  project: ProjectShotVideoLike,
  shotVideoModels: readonly DurationModelLike[] | null | undefined,
): readonly number[] {
  const match = projectShotVideoModel(project, shotVideoModels)
  if (match) return durationOptionsForModel(match)
  return durationOptionsForProvider(
    project.effective_video_generation_provider ?? project.video_generation_provider ?? "kling",
  )
}

/** Nearest allowed duration; if `current` is already allowed, returns it. */
export function nearestDurationSec(current: number | null, options: readonly number[]): number | null {
  if (options.length === 0) return null
  if (current != null && options.includes(current)) return current
  if (current == null) return options[0] ?? null

  let best = options[0]!
  let bestDist = Math.abs(best - current)
  for (let i = 1; i < options.length; i++) {
    const d = options[i]!
    const dist = Math.abs(d - current)
    if (dist < bestDist) {
      best = d
      bestDist = dist
    }
  }
  return best
}

/** Median-ish default: nearest option to the arithmetic mean of allowed durations. */
export function middleDurationSec(options: readonly number[]): number | null {
  if (options.length === 0) return null
  const sum = options.reduce((acc, n) => acc + n, 0)
  return nearestDurationSec(sum / options.length, options)
}

/**
 * Default for "create shot" forms: keep preferred if it is allowed, otherwise middle of options
 * (avoids snapping invalid values like 30s up to the model max).
 */
export function defaultCreateDurationSec(preferred: number | null | undefined, options: readonly number[]): number {
  if (preferred != null && options.includes(preferred)) return preferred
  return middleDurationSec(options) ?? 5
}

export function coerceDurationSec(current: number | null, options: readonly number[]): number | null {
  return nearestDurationSec(current, options)
}

/**
 * Seedance video editing: sentinel -1 ("Auto") makes the output follow the input video
 * (the client also forces ratio "adaptive"). Contextual UI option — never added to catalog durations.
 */
export const ADAPTIVE_DURATION_SEC = -1

// Mirror of seedance_canvas_supports_reference_mode? (mock_pipeline.rb): editing is a 2.x capability.
const SEEDANCE_2_MODEL_PATTERN = /seedance-2|dreamina-seedance-2/i
const SEEDANCE_MODEL_PATTERN = /seedance/i
const SEEDANCE_25_MODEL_PATTERN = /seedance-2[.-]5/i
const KLING_O1_MODEL_PATTERN = /kling-video-o1/i

export const DEFAULT_VIDEO_REF_MAX_SECONDS = 15
export const SEEDANCE_25_VIDEO_REF_MAX_SECONDS = 30
export const KLING_O1_VIDEO_REF_MAX_SECONDS = 10
export const VIDEO_REF_MIN_SECONDS = 2

/** Per-file video-reference max seconds for the current generation model. */
export function maxVideoRefSeconds(model: DurationModelLike | null | undefined): number {
  const catalog = Number(model?.max_video_ref_seconds)
  if (Number.isFinite(catalog) && catalog > 0) return catalog

  const haystack = [model?.id, model?.wire].filter(Boolean).join(" ")
  if (SEEDANCE_25_MODEL_PATTERN.test(haystack)) return SEEDANCE_25_VIDEO_REF_MAX_SECONDS
  if (KLING_O1_MODEL_PATTERN.test(haystack)) return KLING_O1_VIDEO_REF_MAX_SECONDS
  return DEFAULT_VIDEO_REF_MAX_SECONDS
}

/** Combined video-reference max seconds when the catalog/model declares a total cap. */
export function maxTotalVideoRefSeconds(model: DurationModelLike | null | undefined): number | null {
  const catalog = Number(model?.max_total_video_ref_seconds)
  if (Number.isFinite(catalog) && catalog > 0) return catalog

  const haystack = [model?.id, model?.wire].filter(Boolean).join(" ")
  if (SEEDANCE_MODEL_PATTERN.test(haystack)) return maxVideoRefSeconds(model)
  return null
}

/** True when the model catalog declares a positive audio-reference slot count. */
export function modelSupportsAudioRefs(model: DurationModelLike | null | undefined): boolean {
  const n = Number(model?.max_audio_refs)
  return Number.isFinite(n) && n > 0
}

export function maxAudioRefs(model: DurationModelLike | null | undefined): number {
  const n = Number(model?.max_audio_refs)
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0
}

export function maxAudioRefSeconds(model: DurationModelLike | null | undefined): number {
  const catalog = Number(model?.max_audio_ref_seconds)
  if (Number.isFinite(catalog) && catalog > 0) return catalog
  return maxVideoRefSeconds(model)
}

export function maxTotalAudioRefSeconds(model: DurationModelLike | null | undefined): number | null {
  const catalog = Number(model?.max_total_audio_ref_seconds)
  if (Number.isFinite(catalog) && catalog > 0) return catalog
  if (!modelSupportsAudioRefs(model)) return null
  return maxAudioRefSeconds(model)
}

export type AudioRefDurationRow = {
  media_type?: string | null
  duration_seconds?: number | null
}

export function sumAudioRefSeconds(rows: readonly AudioRefDurationRow[] | null | undefined): number {
  return (rows ?? []).reduce((sum, row) => {
    if (row.media_type !== "audio") return sum
    const seconds = Number(row.duration_seconds)
    return Number.isFinite(seconds) && seconds > 0 ? sum + seconds : sum
  }, 0)
}

export function wouldExceedAudioRefTotal(
  existingSeconds: number,
  addedSeconds: number,
  maxTotal: number | null | undefined,
): boolean {
  if (maxTotal == null || !(maxTotal > 0)) return false
  return existingSeconds + addedSeconds > maxTotal
}

/** Video-slot model id from the canvas composer: selected id on the video tab, else the stored video slot. */
export function composerVideoModelId(draft: {
  mediaKind?: string | null
  modelId?: string | null
  modelIdByKind?: { video?: string | null }
}): string | undefined {
  const stored = draft.modelIdByKind?.video?.toString().trim() || undefined
  if (draft.mediaKind === "video") {
    const selected = draft.modelId?.toString().trim()
    return selected || stored
  }
  return stored
}

export function maxVideoRefSecondsForProject(
  project: ProjectShotVideoLike,
  models: readonly DurationModelLike[] | null | undefined,
): number {
  const match = projectShotVideoModel(project, models)
  if (match) return maxVideoRefSeconds(match)
  return maxVideoRefSeconds({ id: project.effective_video_model ?? undefined })
}

export function maxTotalVideoRefSecondsForProject(
  project: ProjectShotVideoLike,
  models: readonly DurationModelLike[] | null | undefined,
): number | null {
  const match = projectShotVideoModel(project, models)
  if (match) return maxTotalVideoRefSeconds(match)
  return maxTotalVideoRefSeconds({ id: project.effective_video_model ?? undefined })
}

export type VideoRefDurationRow = {
  media_type?: string | null
  duration_seconds?: number | null
}

export function sumVideoRefSeconds(rows: readonly VideoRefDurationRow[] | null | undefined): number {
  return (rows ?? []).reduce((sum, row) => {
    if (row.media_type !== "video") return sum
    const seconds = Number(row.duration_seconds)
    return Number.isFinite(seconds) && seconds > 0 ? sum + seconds : sum
  }, 0)
}

export function wouldExceedVideoRefTotal(
  existingSeconds: number,
  addedSeconds: number,
  maxTotal: number | null | undefined,
): boolean {
  if (maxTotal == null || !(maxTotal > 0)) return false
  return existingSeconds + addedSeconds > maxTotal
}

export function roundedVideoRefSeconds(value: number): number {
  return Math.round(value * 10) / 10
}

export function animaticSegmentExceedsVideoRefMax(startMs: number, endMs: number, maxSeconds: number): boolean {
  return endMs - startMs > Math.round(maxSeconds * 1000)
}

export function animaticSegmentBelowVideoRefMin(
  startMs: number,
  endMs: number,
  minSeconds: number = VIDEO_REF_MIN_SECONDS,
): boolean {
  return endMs - startMs < Math.round(minSeconds * 1000)
}

export function modelSupportsAdaptiveDuration(model: DurationModelLike | null | undefined): boolean {
  const haystack = [model?.id, model?.wire].filter(Boolean).join(" ")
  return model?.provider === "byteplus_seedance_video" && SEEDANCE_2_MODEL_PATTERN.test(haystack)
}

/** The project's effective shot-video model from the workspace catalog, if present. */
export function projectShotVideoModel(
  project: ProjectShotVideoLike,
  shotVideoModels: readonly DurationModelLike[] | null | undefined,
): DurationModelLike | null {
  const modelId = project.effective_video_model?.toString().trim()
  if (!modelId || !shotVideoModels?.length) return null
  return shotVideoModels.find((m) => m.id === modelId) ?? null
}

export function withAdaptiveDurationOption(options: readonly number[], allowed: boolean): readonly number[] {
  return allowed ? [ADAPTIVE_DURATION_SEC, ...options] : options
}

/** Like coerceDurationSec, but keeps the -1 sentinel while adaptive mode is still allowed. */
export function coerceCanvasDurationSec(
  current: number | null,
  options: readonly number[],
  adaptiveAllowed: boolean,
): number | null {
  if (current === ADAPTIVE_DURATION_SEC && adaptiveAllowed) return ADAPTIVE_DURATION_SEC
  return coerceDurationSec(current != null && current > 0 ? current : null, options)
}

/** Animatic clip counts as a video reference when it is sent in a non-audio-only mode. */
export function hasAnimaticVideoReference(extras: Record<string, unknown> | null | undefined): boolean {
  if (!extras || typeof extras !== "object") return false
  return (
    Boolean(extras.seedance_send_animatic_reference) &&
    String(extras.seedance_animatic_reference_mode ?? "video_silent") !== "audio_only"
  )
}

export function hasVideoReferenceForAdaptive(
  videoInputCount: number,
  extras: Record<string, unknown> | null | undefined,
): boolean {
  return videoInputCount > 0 || hasAnimaticVideoReference(extras)
}

export type ShotVideoDurationPatch = {
  duration_target_sec?: number
  seedance_adaptive_duration?: boolean
}

/** Patch for duration popover: sentinel -1 only toggles adaptive; plot duration stays a positive second. */
export function nextShotDurationPatch(args: {
  selected: number
  currentDurationSec: number | null
  adaptiveActive: boolean
}): ShotVideoDurationPatch | null {
  const { selected, currentDurationSec, adaptiveActive } = args
  if (selected === ADAPTIVE_DURATION_SEC) {
    if (adaptiveActive) return null
    return { seedance_adaptive_duration: true }
  }
  const patch: ShotVideoDurationPatch = {}
  if (selected !== currentDurationSec) patch.duration_target_sec = selected
  if (adaptiveActive) patch.seedance_adaptive_duration = false
  if (Object.keys(patch).length === 0) return null
  return patch
}
